import { z } from "zod";
import { toFirestoreSchema } from "../utils/toFirestoreSchema";
import { BioContentSchema } from "./bio.schema";
import { GenderEnumSchema, isGender } from "./gender.schema";

/**
 * Discovery user projection with client-style dates (`z.date()`).
 * Soft-fail arrays so one bad field does not drop the whole doc.
 *
 * Trade-off: `.catch([])` is silent (no logger in `@fitties/shared`). Corrupt arrays become
 * empty lists, which filters treat as real state. Severity differs by field — see
 * SHARED_SCHEMAS_PACKAGE.md Phase 2c / risks (`blockedUsers`, `liked_uids` especially).
 * Functions-side warn logging is deferred until it bites (use `firebase-functions` logger).
 *
 * `desired_genders` is special: element-wise `isGender` filter (not whole-array enum `.catch([])`),
 * because empty prefs disable the gender filter (“show everyone”). One dirty value must not wipe
 * valid prefs. Absence and all-corrupt both render as `[]` until Functions logs that case.
 */
const DiscoveryUserSchema = z.object({
  id: z.string().min(1),
  uid: z.string().optional(),
  gender: GenderEnumSchema.optional(),
  desired_genders: z
    .array(z.unknown())
    .catch([])
    .transform((values) => values.filter(isGender)),
  liked_uids: z.array(z.string()).catch([]),
  rejected_uids: z.array(z.string()).catch([]),
  blockedUsers: z.array(z.string()).catch([]),
  hide_unless_liked: z.boolean().optional(),
  includeInWeeklyPool: z.boolean().optional(),
  isBoosted: z.boolean().optional(),
  deletionRequestedAt: z.date().optional(),
  lastKnownEventEnteredAt: z.date().optional(),
  created_time: z.date().optional(),
  display_name: z.string().optional(),
  bio: z.string().optional(),
  bioContent: BioContentSchema.optional().catch(undefined),
  photo_url: z.string().optional(),
  date_of_birth: z.date().optional(),
});

/**
 * Firestore Admin / emulator discovery projection.
 * Date fields become Timestamp-like via {@link toFirestoreSchema}.
 */
export const DiscoveryUserRecordSchema = toFirestoreSchema(DiscoveryUserSchema);

export type DiscoveryUserRecord = z.infer<typeof DiscoveryUserRecordSchema>;

/**
 * Parse a Firestore user snapshot into a discovery record.
 * Returns null when the document cannot be represented (e.g. empty id).
 */
export function parseDiscoveryUserRecord(id: string, data: unknown): DiscoveryUserRecord | null {
  const base = data !== null && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const result = DiscoveryUserRecordSchema.safeParse({ ...base, id });
  return result.success ? result.data : null;
}

/**
 * Minimal user fields used by handleLike mutual-match path.
 */
export const LikeUserRecordSchema = z.object({
  liked_uids: z.array(z.string()).catch([]),
  display_name: z.string().optional(),
  lastKnownEventId: z.string().optional(),
});

export type LikeUserRecord = z.infer<typeof LikeUserRecordSchema>;

/**
 * Parse a Firestore user snapshot for the handleLike path.
 * Returns null when the payload is not a valid record.
 * Callers must check document existence first — do not pass `undefined` expecting an empty record.
 */
export function parseLikeUserRecord(data: unknown): LikeUserRecord | null {
  const result = LikeUserRecordSchema.safeParse(data);
  return result.success ? result.data : null;
}
